import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { TenantSpaceClass } from '../models/space/tenant-space.class';
import { DEFAULT_FILTER_STATE, DEFAULT_NA_FILTER_OPTION } from './filters-toolbar.const';
import { FilterDataRequest, FilterState, FilterView, SpaceFilter, ToolbarFilterOption } from './filters-toolbar.model';
import { StackPlanViewStateService } from '../state/sp-view-state.service';
import { FiltersHttpService, toFilterOptions } from './filters-http.service';

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  /**
   * Public filter state observable, used to provide current filter state
   */
  public filterState$: Observable<FilterState>;
  /** should emit a signal whenever the filter requires to be reset */
  public resetFilter$: Subject<void> = new Subject<void>();

  /** Filter state behavior subject. This property holds the actual filter state */
  private filterState = new BehaviorSubject<FilterState>(DEFAULT_FILTER_STATE);

  /** per-load in-flight cache, keyed by property/view/locale/type/unaccounted-flag (QW-04) */
  readonly #filterData = new Map<string, Observable<ToolbarFilterOption[]>>();
  /** last requested view, used to warm the cache with the view the toolbar is currently on (QW-04) */
  #lastView: FilterView = FilterView.LeaseYear;

  /** the view the toolbar is currently on, sent as `filterView` on the stack2 include bundle (HV-11F) */
  public get lastView(): FilterView {
    return this.#lastView;
  }

  constructor(
    private filterHttpClient: FiltersHttpService,
    // TODO: refactor to remove this dependency
    private spStateView: StackPlanViewStateService
  ) {
    this.filterState$ = this.filterState.asObservable();
  }

  public setFilterOptions(filterOptions: ToolbarFilterOption[], viewBy: FilterView) {
    this.filterState.next({ ...this.filterState.value, filterOptions, viewBy });
  }
  /**
   * Toggle provided filter on the current filter state. If it is off or
   * undefined, it will create it and assign true, else it will assign false
   */
  public toggleFilter(filterKey: number) {
    const currentFiltersState = this.filterState.value;
    currentFiltersState.selectedFilters[filterKey] = !currentFiltersState.selectedFilters[filterKey];
    this.filterState.next(currentFiltersState);
  }

  /**
   * Remove all filters from current filter state
   */
  public clearSelectedFilters() {
    this.filterState.next({ ...this.filterState.value, selectedFilters: {} });
  }

  getFilterForSpace = (floorNumber: string, spaceItem: TenantSpaceClass, filterSpaces: SpaceFilter) => {
    const { floor, suite, lease } = filterSpaces;
    // There could multiple spaces without suite number for the same floor, so we need to compare based on space id from lease
    if ((spaceItem.suiteNumber == '' || spaceItem.suiteNumber == null) && spaceItem.lease != null) {
      // Compare base on the Space ID from lease
      return spaceItem.lease.space_id === lease?.space_id;
    }
    return suite === spaceItem.suiteNumber && floor === floorNumber;
  };

  /**
   * Given a specific space item, it looks for the specific filter option where
   * the item belongs into.
   *
   * This only works on tenant spaces as those kind of spaces are the only
   * "filterable" items.
   */
  public getItemSchemaClass(
    spaceItem: TenantSpaceClass,
    floorNumber: string,
    altFilterOptions?: ToolbarFilterOption[]
  ): ToolbarFilterOption {
    const filterOptions = altFilterOptions ? altFilterOptions : this.filterState.value.filterOptions;
    const filter: ToolbarFilterOption = (filterOptions || []).find(
      ({ spaces: filterSpaces }) =>
        filterSpaces && filterSpaces.some(fs => this.getFilterForSpace(floorNumber, spaceItem, fs))
    );
    return filter ? filter : DEFAULT_NA_FILTER_OPTION;
  }

  public setFilters(viewBy: FilterView) {
    this.filterState.next({ ...this.filterState.value, viewBy });
  }

  public getFilterData(propertyId: number, options: FilterDataRequest): Observable<ToolbarFilterOption[]> {
    const propertyType = this.spStateView.getState().propertyType;
    this.#lastView = options.view ?? this.#lastView;
    const key = this.#buildCacheKey(
      propertyId,
      options.view,
      options.localeId,
      propertyType,
      options.isRemoveUnaccountedEnabled
    );
    if (!this.#filterData.has(key)) {
      this.#filterData.set(
        key,
        this.filterHttpClient.getFilterData(propertyId, { ...options, propertyType }).pipe(
          catchError(err => {
            this.#filterData.delete(key);
            return throwError(() => err);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        )
      );
    }
    return this.#filterData.get(key);
  }

  /**
   * Warms the filter-data cache for the current load, using the last view the
   * toolbar was on (default `LeaseYear`), so the toolbar's own request on init
   * hits the cache instead of firing a second network call (QW-04).
   */
  public prefetch(propertyId: number, localeId: number): void {
    this.getFilterData(propertyId, { view: this.#lastView, localeId }).subscribe({ error: () => {} });
  }

  /**
   * Seeds the filter-data cache directly from the stack2 include=filters bundle (HV-11F), so the
   * toolbar's own `getFilterData` call hits the cache instead of firing `/stack2/{id}/filters`.
   *
   * `view`/`propertyType`/`localeId` MUST be the exact values the stack2 request that returned
   * `rawFilters` was sent with, supplied by the caller (`StackingPlanService.getStackingPlan`
   * captures them when it builds that request) - never re-derived here from `#lastView` or
   * `StackPlanViewStateService` at call time. A stack2 response can land long after the toolbar
   * has moved the user to a different view/property type (its own `getFilterData` call updates
   * `#lastView` in ~250ms, well before a ~2.4s stack2 response), so reading "current" state here
   * would silently seed the OLD response's filters under the CURRENT (wrong) key.
   *
   * Uses the same mapping (`toFilterOptions`) the dedicated endpoint's response goes through, so
   * a seeded and a network-fetched result are indistinguishable to callers.
   */
  public seedFilterData(
    propertyId: number,
    view: FilterView,
    propertyType: string | null | undefined,
    localeId: number | undefined,
    rawFilters: ToolbarFilterOption[]
  ): void {
    // The include bundle only ever satisfies the toolbar's default getFilterData call, never the
    // exports' isRemoveUnaccountedEnabled=true request, so this is always unset here.
    const isRemoveUnaccountedEnabled = undefined;
    const key = this.#buildCacheKey(propertyId, view, localeId, propertyType, isRemoveUnaccountedEnabled);
    this.#filterData.set(key, of(toFilterOptions(rawFilters, { isRemoveUnaccountedEnabled })));
  }

  /** Clears the per-load filter-data cache (QW-04). */
  public clearCache(): void {
    this.#filterData.clear();
  }

  readonly #buildCacheKey = (
    propertyId: number,
    view: FilterView,
    localeId: number | undefined,
    propertyType: string | null | undefined,
    isRemoveUnaccountedEnabled: boolean | undefined
  ): string => `${propertyId}|${view}|${localeId}|${propertyType}|${isRemoveUnaccountedEnabled ?? ''}`;
}
